"use client";

import type React from "react";

import { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  Autocomplete,
  Chip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  CalendarMonth as CalendarIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  AccessTime as AccessTimeIcon,
  Share as ShareIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Email as EmailIcon,
  AutoAwesome as AutoAwesomeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  UploadFile as UploadFileIcon,
  Info as InfoIcon, // Added InfoIcon for admin notes
  Cancel as CancelIcon, // Added CancelIcon for rejected status
  HourglassEmpty as HourglassEmptyIcon, // Added for awaiting approval
  CheckCircleOutline as CheckCircleOutlineIcon, // Added for approved
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router";
import { useUser } from "@/contexts/UserContext";
import axios from "axios";
import { apiRemote, IProposalFormEdit } from "@/api";
import { useAlert } from "@/contexts/AlertContext";
import useDebounce from "@/hooks/use-debounce";
import { EditToggle, EditType } from "@/components/EditToggle";

interface Educator {
  ID: string;
  user_login: string;
  user_pass: string;
  user_nicename: string;
  user_email: string;
  user_url: string;
  user_registered: string;
  user_activation_key: string;
  user_status: string;
  display_name: string;
  status: string;
}

interface Topic {
  id: number | string;
  name: string;
}



const initialProposalFormData: IProposalFormEdit = {
  id: 0,
  title: "",
  description: "",
  username: "",
  user_fullname: "",
  user_email: "",
  learning_objectives: "",
  date: "",
  format: "online",
  target_audience: "",
  duration: 0,
  category_id: 0,
  max_attendee: 0,
  image_url: null,
  event_link: "",
  topics: [] as Topic[],
  status: "", // Initialize status
  reviewed_by: null,
  reviewed_at: null,
  admin_notes: null,
  approved_workshop_id: null,
  created_at: "",
  updated_at: "",
  image_url_old: null,
  current_user: ""
};

const STATUS_AWAITING_APPROVAL = "awaiting_approval";
const STATUS_APPROVED = "approved";
const STATUS_REJECTED = "rejected";
const STATUS_REVISION = "revision";
const STATUS_INACTIVE = "inactive";

export default function ProposalDetail() {
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categories, setCategories] = useState<any[]>([]);
  const [newCategory, setNewCategory] = useState("");
  const [aiImagePrompt, setAiImagePrompt] = useState("");
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [isGeneratingObjectives, setIsGeneratingObjectives] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);

  const [educators, setEducators] = useState<any[]>([]);
  const [searchEducators, setSearchEducators] = useState<string>("");
  const debouncedSearchTerm = useDebounce(searchEducators, 500);
  const [selectedEducator, setSelectedEducator] = useState<Educator | null>(
    null
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [topics, setTopics] = useState<any[]>([]);
  const { id } = useParams<{ id: string }>();

  const alert = useAlert();
  const navigate = useNavigate();
  const { user } = useUser();

  const isAdminOrOrg = user?.is_admin || user?.is_org;

  const [proposalData, setProposalData] = useState<IProposalFormEdit>(
    initialProposalFormData
  );
  const [loading, setLoading] = useState<boolean>(false);

  // Determine if the form should be editable
  const isEditable = isAdminOrOrg || proposalData.status === STATUS_REVISION;

  if (user?.user_login == null || typeof user?.user_login == "undefined") {
    return (
      <Container maxWidth="lg" sx={{ textAlign: "center", py: 5 }}>
        <Typography variant="h5" color="error">
          Please login to view proposal details.
        </Typography>
      </Container>
    );
  }

  useEffect(() => {
    if (!id) {
      setLoading(false);
      alert.error("No proposal ID provided.");
      return;
    }
    setLoading(true);
    apiRemote
      .getProposalById(id, user.user_login)
      .then((data) => {
        if (data && data.data) {
          const processedDescription = data.data.description.replace(
            /\\'/g,
            "'"
          );
          let parsedTopics: Topic[] = [];
          if (data.data.topics && typeof data.data.topics === "string") {
            try {
              parsedTopics = JSON.parse(data.data.topics);
            } catch (e) {
              console.error("Error parsing topic string:", e);
              alert.error("Failed to parse proposal topics.");
            }
          } else if (Array.isArray(data.data.topics)) {
            parsedTopics = data.data.topics;
          }

          setProposalData({
            ...data.data,
            description: processedDescription,
            topics: parsedTopics,
            image_url: data.data.image_url,
            image_url_old: data.data.image_url,
            category_id: data.data.category_id,
            username: data.data.username,
            user_fullname: data.data.user_fullname,
            user_email: data.data.user_email,
            learning_objectives: data.data.learning_objectives,
            status: data.data.status, // Ensure status is set
            admin_notes: data.data.admin_notes, // Ensure admin_notes is set
            current_user: user.user_login
          });

          if (data.data.image_url) {
            setSelectedImage(data.data.image_url);
          }
        }
      })
      .catch((err) => {
        alert.error(err.message || "Failed to fetch proposal details.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, user.user_login]);

  useEffect(() => {
    apiRemote
      .getListCategory()
      .then((data) => {
        setCategories(data.data || []);
      })
      .catch((error) => {
        console.error("Error fetching categories:", error);
        setCategories([]);
      });
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !proposalData.title ||
      !proposalData.description ||
      !proposalData.date ||
      !(proposalData.topics as Topic[]).length ||
      !proposalData.duration ||
      !proposalData.format ||
      !proposalData.category_id ||
      !proposalData.max_attendee ||
      !proposalData.learning_objectives || 
      !proposalData.target_audience
    ) {
      alert.error("Please fill in all required information.");
      return;
    }

    if (!proposalData.image_url) {
      alert.error("Please upload an image for the proposal.");
      return;
    }

    setIsSubmitting(true);

    const dataToSubmit = {
      ...proposalData,
      topics: proposalData.topics,
      category_id: Number(proposalData.category_id),
      duration: Number(proposalData.duration),
      max_attendee: Number(proposalData.max_attendee),
    };
    // console.log(dataToSubmit)
    apiRemote
      .updateProposal(dataToSubmit)
      .then((data) => {
        if (data.code === 200 || data.code === 201) {
          alert.success(data.message || "Proposal updated successfully!");
          // Consider re-fetching data after successful update to reflect new status/notes
          // window.location.reload(); // Simple reload for demo, consider more refined state management
        } else {
          alert.error(data.message || "Failed to update proposal.");
        }
      })
      .catch((err) => {
        alert.error(err.message || "Failed to update proposal.");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleGenerateSummary = async () => {
    if (!proposalData.title.trim()) {
      alert.warning(
        "Please enter a Proposal Title first to generate a summary."
      );
      return;
    }
    setIsGeneratingSummary(true);
    try {
      const response = await apiRemote.generateSummary({
        title: proposalData.title,
      });
      if(response.data.summary){
        setProposalData((prev) => ({
          ...prev,
          description: response.data.summary,
        }));
      }else{
        alert.error("We're sorry, we are currently experiencing an issue. Please try again in a few minutes.")
      }
    } catch (error) {
      console.error("Error generating summary:", error);
      alert.error("Failed to generate summary. Please try again.");
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const handleGenerateObjectives = async () => {
    if (!proposalData.title.trim() || !proposalData.description.trim()) {
      alert.warning(
        "Please enter a Proposal Title and Brief Summary first to generate learning objectives."
      );
      return;
    }
    setIsGeneratingObjectives(true);
    try {
      const response = await apiRemote.generateLearningObjective({
        title: proposalData.title,
        summary: proposalData.description,
      });
      if (response.data && Array.isArray(response.data.learning_objectives)) {
        const formattedObjectives = response.data.learning_objectives
          .map((obj: string) => `- ${obj}`)
          .join("\n");
        setProposalData((prev) => ({
          ...prev,
          learning_objectives: formattedObjectives,
        }));
      } else {
        alert.error("Invalid response format for learning objectives.");
      }
    } catch (error) {
      console.error("Error generating objectives:", error);
      alert.error("Failed to generate learning objectives. Please try again.");
    } finally {
      setIsGeneratingObjectives(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!aiImagePrompt.trim()) {
      alert.warning("Please enter a prompt to generate an image.");
      return;
    }
    setIsGeneratingImage(true);
    try {
      const response = await apiRemote.generateImage({
        prompt: aiImagePrompt,
      });
      setSelectedImage(response.data.image_urls[0]);
      setProposalData((prev) => ({
        ...prev,
        image_url: response.data.image_urls[0],
      }));
    } catch (error) {
      console.error("Error generating image:", error);
      alert.error("Failed to generate image. Please try again.");
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result as string);
      };
      reader.readAsDataURL(file);

      setProposalData((prev) => ({
        ...prev,
        image_url: file as any,
      }));
    }
  };

  const handleTopicsChange = (
    event: any,
    newValue: (string | { id: number | string; name: string })[]
  ) => {
    const formattedTopics: Topic[] = newValue.map((topic) =>
      typeof topic === "string" ? { id: Date.now(), name: topic } : topic
    );
    setProposalData((prev) => ({
      ...prev,
      topics: formattedTopics,
    }));
  };

  const getStatusDisplay = (status: string) => {
    let color = "grey";
    let icon = <InfoIcon />;
    let text = "Unknown Status";

    switch (status) {
      case STATUS_AWAITING_APPROVAL:
        color = "warning.main";
        icon = <HourglassEmptyIcon />;
        text = "Awaiting Approval";
        break;
      case STATUS_APPROVED:
        color = "success.main";
        icon = <CheckCircleOutlineIcon />;
        text = "Approved";
        break;
      case STATUS_REJECTED:
        color = "error.main";
        icon = <CancelIcon />;
        text = "Rejected";
        break;
      case STATUS_REVISION:
        color = "info.main";
        icon = <EditIcon />;
        text = "Revision Needed";
        break;
      case STATUS_INACTIVE:
        color = "text.secondary";
        icon = <InfoIcon />;
        text = "Inactive";
        break;
      default:
        break;
    }
    return (
      <Chip
        icon={icon}
        label={text}
        sx={{ bgcolor: color, color: 'white', fontWeight: 'bold' }}
      />
    );
  };

  if (loading) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
        }}
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading Proposal Data...
        </Typography>
      </Container>
    );
  }

  if(proposalData.username && user.user_login != proposalData.username){
    return (
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
        }}
      >
        <Typography variant="h6" sx={{ ml: 2 }}>
          You do not have permission to view this proposal.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3 }}
      >
        Go Back
      </Button>
      <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h3" gutterBottom fontWeight={500}>
          Proposal Details
        </Typography>
        {proposalData.status && getStatusDisplay(proposalData.status)}
      </Box>

      {(proposalData.status === STATUS_REJECTED ||
      proposalData.status === STATUS_REVISION) &&
      proposalData.admin_notes && (
        <Card
          sx={{
            mb: 3,
            // Màu nền và border sẽ thay đổi tùy thuộc vào trạng thái
            bgcolor:
              proposalData.status === STATUS_REJECTED
                ? "error.lightest"
                : "info.lightest",
            borderColor:
              proposalData.status === STATUS_REJECTED
                ? "error.main"
                : "info.main",
            border: 1,
          }}
        >
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              {proposalData.status === STATUS_REJECTED ? (
                <CancelIcon color="error" sx={{ mr: 1 }} />
              ) : (
                <EditIcon color="info" sx={{ mr: 1 }} />
              )}
              <Typography
                variant="h6"
                color={
                  proposalData.status === STATUS_REJECTED ? "error.main" : "info.main"
                }
                fontWeight={600}
              >
                {proposalData.status === STATUS_REJECTED
                  ? "Reason for Rejection:"
                  : "Reason for Revision Request:"}
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ ml: 3 }}>
              {proposalData.admin_notes}
            </Typography>
          </CardContent>
        </Card>
      )}

      <Card>
        <Box component="form" onSubmit={handleSubmit}>
          {/* Section 1: Proposal Content */}
          <CardContent sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <Box
                sx={{
                  bgcolor: "primary.main",
                  color: "white",
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                }}
              >
                1
              </Box>
              <Typography variant="h4" fontWeight={500}>
                Proposal Content
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="Proposal Title"
              placeholder="e.g., The Official TCC Debrief"
              required
              sx={{
                mb: 3,
                // Truy cập vào lớp CSS của dấu hoa thị
                '& .MuiInputLabel-asterisk': {
                  color: 'red', // Đổi màu dấu hoa thị thành đỏ
                },
              }}
              value={proposalData.title}
              onChange={(e) =>
                setProposalData((prev) => ({
                  ...prev,
                  title: e.target.value,
                }))
              }
              InputProps={{
                readOnly: !isEditable,
              }}
            />

            <TextField
              fullWidth
              label="Brief Summary"
              placeholder="A short, catchy summary for the proposal card..."
              multiline
              required
              value={proposalData.description}
              minRows={3}
              maxRows={10}
              onChange={(e) =>
                setProposalData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              sx={{
                mb: 3,
                // Truy cập vào lớp CSS của dấu hoa thị
                '& .MuiInputLabel-asterisk': {
                  color: 'red', // Đổi màu dấu hoa thị thành đỏ
                },
              }}
              InputProps={{
                readOnly: !isEditable,
                endAdornment: isEditable && (
                  <Button
                    startIcon={
                      isGeneratingSummary ? (
                        <CircularProgress size={20} />
                      ) : (
                        <AutoAwesomeIcon />
                      )
                    }
                    size="small"
                    onClick={handleGenerateSummary}
                    disabled={isGeneratingSummary || !proposalData.title.trim()}
                  >
                    {isGeneratingSummary ? "Drafting..." : "Draft with AI"}
                  </Button>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Learning Objectives"
              placeholder="- Identify 3-5 practical AI tools..."
              multiline
              required
              sx={{
                mb: 3,
                // Truy cập vào lớp CSS của dấu hoa thị
                '& .MuiInputLabel-asterisk': {
                  color: 'red', // Đổi màu dấu hoa thị thành đỏ
                },
              }}
              minRows={5}
              maxRows={15}
              value={proposalData.learning_objectives}
              onChange={(e) =>
                setProposalData((prev) => ({
                  ...prev,
                  learning_objectives: e.target.value,
                }))
              }
              InputProps={{
                readOnly: !isEditable,
                endAdornment: isEditable && (
                  <Button
                    startIcon={
                      isGeneratingObjectives ? (
                        <CircularProgress size={20} />
                      ) : (
                        <AutoAwesomeIcon />
                      )
                    }
                    size="small"
                    onClick={handleGenerateObjectives}
                    disabled={
                      isGeneratingObjectives ||
                      !proposalData.title.trim() ||
                      !proposalData.description.trim()
                    }
                  >
                    {isGeneratingObjectives ? "Generating..." : "Generate"}
                  </Button>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Target Audience"
              required
              placeholder="e.g., K-12 Science Teachers, School Administrators..."
              multiline
              rows={2}
              sx={{
                // Truy cập vào lớp CSS của dấu hoa thị
                '& .MuiInputLabel-asterisk': {
                  color: 'red', // Đổi màu dấu hoa thị thành đỏ
                },
              }}
              value={proposalData.target_audience}
              onChange={(e) =>
                setProposalData((prev) => ({
                  ...prev,
                  target_audience: e.target.value,
                }))
              }
              InputProps={{
                readOnly: !isEditable,
              }}
            />
          </CardContent>

          {/* Section 2: Logistics & Settings */}
          <CardContent sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <Box
                sx={{
                  bgcolor: "primary.main",
                  color: "white",
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                }}
              >
                2
              </Box>
              <Typography variant="h4" fontWeight={500}>
                Logistics & Settings
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Proposal Date"
                  type="datetime-local"
                  value={proposalData.date}
                  onChange={(e) =>
                    setProposalData({ ...proposalData, date: e.target.value })
                  }
                  sx={{
                    // Truy cập vào lớp CSS của dấu hoa thị
                    '& .MuiInputLabel-asterisk': {
                      color: 'red', // Đổi màu dấu hoa thị thành đỏ
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                  required
                  InputProps={{
                    readOnly: !isEditable,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Duration (in minutes)"
                  type="number"
                  placeholder="e.g., 60"
                  required
                  value={proposalData.duration}
                  onChange={(e) =>{
                    const value = Number(e.target.value);
                    // Chỉ cập nhật giá trị nếu nó không âm
                    if (value >= 0) {
                      setProposalData((prev) => ({
                        ...prev,
                        duration: value,
                      }));
                    }
                  }}
                  sx={{
                    // Truy cập vào lớp CSS của dấu hoa thị
                    '& .MuiInputLabel-asterisk': {
                      color: 'red', // Đổi màu dấu hoa thị thành đỏ
                    },
                  }}
                  InputProps={{
                    readOnly: !isEditable,
                    inputProps: { min: 0 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={!isEditable}>
                  <InputLabel>Format <span style={{color: "red"}}>*</span></InputLabel>
                  <Select
                    defaultValue="online"
                    label="Format"
                    required
                    value={proposalData.format}
                    onChange={(e) =>
                      setProposalData((prev) => ({
                        ...prev,
                        format: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="online">Online</MenuItem>
                    <MenuItem value="in-person">In-Person</MenuItem>
                    <MenuItem value="hybrid">Hybrid</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={!isEditable}>
                  <InputLabel>Category <span style={{color: "red"}}>*</span></InputLabel>
                  <Select
                    label="Category"
                    required
                    value={proposalData.category_id}
                    onChange={(e) =>
                      setProposalData((prev) => ({
                        ...prev,
                        category_id: Number(e.target.value),
                      }))
                    }
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Max Attendee Limit"
                  type="number"
                  placeholder="Leave blank for unlimited"
                  required
                  value={proposalData.max_attendee}
                  onChange={(e) =>{
                    const value = Number(e.target.value);
                    // Chỉ cập nhật giá trị nếu nó không âm
                    if (value >= 0) {
                      setProposalData((prev) => ({
                        ...prev,
                        max_attendee: value,
                      }));
                    }
                  }}
                  sx={{
                    // Truy cập vào lớp CSS của dấu hoa thị
                    '& .MuiInputLabel-asterisk': {
                      color: 'red', // Đổi màu dấu hoa thị thành đỏ
                    },
                  }}
                  InputProps={{
                    readOnly: !isEditable,
                    inputProps: { min: 0 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Live Event Link"
                  type="text"
                  value={proposalData.event_link}
                  onChange={(e) =>
                    setProposalData((prev) => ({
                      ...prev,
                      event_link: e.target.value,
                    }))
                  }
                  InputProps={{
                    readOnly: !isEditable,
                  }}
                  sx={{
                    // Truy cập vào lớp CSS của dấu hoa thị
                    '& .MuiInputLabel-asterisk': {
                      color: 'red', // Đổi màu dấu hoa thị thành đỏ
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={topics}
                  getOptionLabel={(option: any) => option.name || option}
                  value={proposalData.topics as Topic[]}
                  isOptionEqualToValue={(option, value) =>
                    (option as Topic).id === (value as Topic).id ||
                    (option as Topic).name === (value as Topic).name
                  }
                  onChange={handleTopicsChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={
                        <>
                          Topics <span style={{ color: "red" }}>*</span>
                        </>
                      }
                      // Thêm helperText ở đây để hướng dẫn người dùng
                      helperText="Type a topic and press Enter. Do the same for each topic you want to add."
                      sx={{
                        '& .MuiFormHelperText-root': {
                          color: 'blue', // Ví dụ: đổi màu chữ thành xanh
                          // fontWeight: 'bold', // Ví dụ: in đậm chữ
                          fontSize: '0.9rem', // Ví dụ: tăng kích thước chữ
                          // Bạn có thể thêm các thuộc tính CSS khác tùy ý
                        },
                      }}
                    />
                  )}
                  renderTags={(topicValue, getTagProps) =>
                    topicValue.map((option, index) => (
                      <Chip
                        label={(option as Topic).name || (option as string)}
                        {...getTagProps({ index })}
                      />
                    ))
                  }
                  readOnly={!isEditable}
                />
              </Grid>
            </Grid>
          </CardContent>

          {/* Section 3: Cover Image */}
          <CardContent sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
              <Box
                sx={{
                  bgcolor: "primary.main",
                  color: "white",
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                }}
              >
                3
              </Box>
              <Typography variant="h4" fontWeight={500}>
                Cover Image
              </Typography>
            </Box>

            <TextField
              fullWidth
              label="Generate Image with AI"
              placeholder="e.g., 'A modern classroom with glowing data visualizations'"
              value={aiImagePrompt}
              onChange={(e) => setAiImagePrompt(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                readOnly: !isEditable,
                endAdornment: isEditable && (
                  <Button
                    startIcon={
                      isGeneratingImage ? (
                        <CircularProgress size={20} />
                      ) : (
                        <AutoAwesomeIcon />
                      )
                    }
                    size="small"
                    onClick={handleGenerateImage}
                    disabled={isGeneratingImage || !aiImagePrompt.trim()}
                  >
                    {isGeneratingImage ? "Generating..." : "Generate"}
                  </Button>
                ),
              }}
            />

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                (or)
              </Typography>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                disabled={!isEditable}
              >
                Upload image manually
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageUpload}
                />
              </Button>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                {selectedImage ? (
                  <Box
                    component="img"
                    src={selectedImage}
                    alt="Selected Cover Image"
                    sx={{
                      width: "100%",
                      borderRadius: 1,
                      border: 3,
                      borderColor: "primary.main",
                      minHeight: 300,
                      objectFit: "fill",
                      maxHeight: 700
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: 169,
                      borderRadius: 1,
                      border: 3,
                      borderColor: "divider",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "grey.100",
                      color: "text.secondary",
                    }}
                  >
                    <Typography variant="body2">No image selected</Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </CardContent>

          {/* Actions */}
          <Box
            sx={{
              p: 3,
              bgcolor: "grey.50",
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button type="submit" variant="contained" disabled={isSubmitting || !isEditable}>
              {isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Update Proposal"
              )}
            </Button>
          </Box>
        </Box>
      </Card>
    </Container>
  );
}